import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MessagesSquare, Mail, Headset, Archive, Users } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Carregando, Vazio } from '../../componentes/estados';
import { api, get, mensagemDeErro } from '../../nucleo/api';
import { Alternador, Selecao } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useDiscreto } from '../../componentes/sensivel';
import { useToast } from '../../componentes/toast';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { email as fmtEmail, relativo } from '../../nucleo/formato';
import { AvisoConector, useEstadoCanais, useIntervaloConversas } from './dados';
import { ListaConversas, type EstadoLista } from './ListaConversas';
import { PainelConversa } from './PainelConversa';
import { AcessoCaixasModal, caixaComAcesso, seloTipoCaixa } from './AcessoCaixas';
import { CHAVE_CAIXAS_EMAIL, tamanhoUso, type Canal, type CaixaEmail, type Conversa, type EstadoCaixas } from './tipos';

/** U11 (lote 1): retenção de cada caixa monitorada (quem edita o Conversas); só as caixas que mudaram vão ao servidor. */
export function RetencaoModal({ dados, aberto, aoFechar }: { dados: EstadoCaixas; aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation('conversas');
  useDiscreto(); // Review r63 (VIS-M1): o endereço da caixa passa pelo `email()` e redesenha quando o discreto muda.
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const inicial = () => Object.fromEntries(dados.caixas.map((c) => [c.endereco, c.retencao_meses ? String(c.retencao_meses) : '']));
  const [valores, setValores] = useState<Record<string, string>>(inicial);
  const salvar = useMutation({
    mutationFn: async () => {
      for (const c of dados.caixas) {
        const novo = valores[c.endereco] ?? '';
        if (novo === (c.retencao_meses ? String(c.retencao_meses) : '')) continue;
        await api('PUT', '/conversas/email/caixas/retencao', { endereco: c.endereco, retencao_meses: novo ? Number(novo) : null });
      }
    },
    onSuccess: () => {
      toast.sucesso(t('u11.retencaoSalva'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
    onSettled: () => qc.invalidateQueries({ queryKey: CHAVE_CAIXAS_EMAIL }),
  });
  const opcoes = [{ valor: '', rotulo: t('u11.opcaoGuardaTudo') }, ...(dados.retencoes ?? [3, 6, 12, 24, 36, 60]).map((m) => ({ valor: String(m), rotulo: t('u11.opcaoMeses', { count: m }) }))];
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('u11.retencaoTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinta-3">{t('u11.retencaoTexto')}</p>
        {dados.caixas.map((c) => (
          <label key={c.endereco} className="flex min-w-0 flex-col gap-1.5 text-sm font-medium text-tinta-2">
            <span className="min-w-0 font-mono break-all">{fmtEmail(c.endereco)}</span>
            <Selecao opcoes={opcoes} value={valores[c.endereco] ?? ''} onChange={(e) => setValores((v) => ({ ...v, [c.endereco]: e.target.value }))} />
          </label>
        ))}
      </div>
    </Modal>
  );
}

/** U11 (lote 1): uso de disco e retenção de uma caixa, ao lado do estado da sincronização. */
function UsoCaixa({ c, usoVisivel }: { c: CaixaEmail; usoVisivel: boolean }) {
  const { t, i18n } = useTranslation('conversas');
  const retencao = c.retencao_meses ? t('u11.retencaoMeses', { count: c.retencao_meses }) : t('u11.guardaTudo');
  // Review r56 (SEG-B1): quem não vê todas as empresas não recebe o uso (que soma todas); só a retenção.
  if (!usoVisivel) return <span className="text-tinta-3">{retencao}</span>;
  if (!c.uso) return <span className="text-tinta-3">{`${t('u11.semUso')}, ${retencao}`}</span>;
  const partes = [t('u11.noServidor', { tamanho: tamanhoUso(c.uso.texto + c.uso.anexos, i18n.language) }), ...(c.uso.provedor ? [t('u11.noProvedor', { tamanho: tamanhoUso(c.uso.provedor, i18n.language) })] : []), retencao];
  return (
    <span className="text-tinta-3" title={t('u11.usoTitulo', { count: c.uso.mensagens, quando: relativo(c.uso.calculado_em) })} data-uso-caixa>
      {partes.join(', ')}
    </span>
  );
}

/** Fase 2 (W9, F1d): caixas de e-mail monitoradas e a última sincronização de cada uma (U11: com o uso e a retenção). */
export function CaixasEmail() {
  const { t } = useTranslation('conversas');
  useDiscreto(); // Review r63 (VIS-M1): o endereço da caixa passa pelo `email()` e redesenha quando o discreto muda.
  const s = useSessao();
  const [retencao, setRetencao] = useState(false);
  const [acesso, setAcesso] = useState(false);
  const q = useQuery({ queryKey: CHAVE_CAIXAS_EMAIL, queryFn: () => get<EstadoCaixas>('/conversas/email/caixas'), staleTime: 30_000 });
  if (!q.data?.caixas.length) return null;
  // U11 (lote 2): a retenção só nas caixas que quem pede lê (a pessoal alheia aparece só para dizer de quem é).
  const legiveis = { ...q.data, caixas: q.data.caixas.filter((c) => c.le !== false) };
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-borda bg-sup-1 px-4 py-2 text-xs text-tinta-2">
      <Mail className="size-4 shrink-0 text-tinta-3" aria-hidden />
      <span className="font-medium">{t('w9.caixasMonitoradas')}:</span>
      {q.data.caixas.map((c) => (
        <span key={c.endereco} className="min-w-0 break-words">
          <span className="font-mono break-all">{fmtEmail(c.endereco)}</span> {seloTipoCaixa(c, t) && <span className="text-tinta-3">{seloTipoCaixa(c, t)}</span>}{' '}
          <span className={c.ultimo_erro ? 'text-perigo' : 'text-tinta-3'}>({c.ultimo_erro ? t('w9.comErro') : c.sincronizado_em ? t('w9.sincronizada', { quando: relativo(c.sincronizado_em) }) : t('w9.nuncaSincronizada')})</span>{' '}
          <UsoCaixa c={c} usoVisivel={q.data.uso_visivel !== false} />
        </span>
      ))}
      {/* Review r56 (VIS-M1): a retenção vale para a caixa inteira, de todas as empresas: só quem pode no workspace. */}
      <span className="ml-auto flex flex-wrap gap-1">
        {/* U11 (lote 2): tipo, dono e delegações (quem configura, o dono da pessoal, quem delega a corporativa ou é delegado). */}
        {q.data.caixas.some(caixaComAcesso) && (
          <Botao tamanho="sm" variante="fantasma" icone={<Users className="size-4" />} onClick={() => setAcesso(true)}>
            {t('u11l2.acesso')}
          </Botao>
        )}
        {podeNoWorkspace(s, 'conversas.configurar') && legiveis.caixas.length > 0 && (
          <Botao tamanho="sm" variante="fantasma" icone={<Archive className="size-4" />} onClick={() => setRetencao(true)}>
            {t('u11.retencao')}
          </Botao>
        )}
      </span>
      {retencao && <RetencaoModal dados={legiveis} aberto={retencao} aoFechar={() => setRetencao(false)} />}
      {acesso && <AcessoCaixasModal dados={q.data} aberto={acesso} aoFechar={() => setAcesso(false)} />}
    </div>
  );
}

interface Presenca {
  marcado: boolean;
  disponivel: boolean;
}

/** A presença vence em 3 minutos no servidor; a caixa aberta renova a cada minuto. */
const RENOVAR_PRESENCA_MS = 60_000;

/**
 * Trilha 5b (Digisac): Disponível ou Ausente para a distribuição por menor carga. Com a caixa aberta e Disponível, a tela
 * renova a presença a cada minuto; fechada a caixa, a pessoa deixa de receber conversas em até 3 minutos.
 */
function PresencaAtendimento() {
  const { t } = useTranslation('conversas');
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['conversas', 'presenca'], queryFn: () => get<Presenca>('/conversas/presenca') });
  const marcar = useMutation({
    mutationFn: (disponivel: boolean) => api<Presenca>('PUT', '/conversas/presenca', { disponivel }),
    onSuccess: (p) => qc.setQueryData(['conversas', 'presenca'], p),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // Review r46 robustez (B5): a renovação só estende a presença (`renovar`), sem mandar o valor: o "Ausente" marcado em outra
  // aba ou no celular não é desfeito por esta. A resposta traz o que está marcado agora.
  const renovacao = useMutation({
    mutationFn: () => api<Presenca>('PUT', '/conversas/presenca', { renovar: true }),
    onSuccess: (p) => qc.setQueryData(['conversas', 'presenca'], p),
  });
  const marcado = !!q.data?.marcado;
  const renovar = renovacao.mutate;
  useEffect(() => {
    if (!marcado) return;
    // Abrir a caixa já renova (a presença pode ter vencido com a caixa fechada).
    renovar();
    const id = setInterval(() => renovar(), RENOVAR_PRESENCA_MS);
    return () => clearInterval(id);
  }, [marcado, renovar]);
  if (!q.data) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-borda bg-sup-1 px-4 py-2 text-xs text-tinta-2">
      <Headset className="size-4 shrink-0 text-tinta-3" aria-hidden />
      <label htmlFor="conversas-presenca" className="font-medium">
        {t('t5b.presenca')}
      </label>
      <Alternador id="conversas-presenca" rotulo={t('t5b.presenca')} marcado={marcado} aoMudar={(v) => marcar.mutate(v)} />
      <span className="min-w-0 flex-1 basis-64 text-tinta-3">{t('t5b.presencaAjuda')}</span>
    </div>
  );
}

/**
 * Caixa unificada de um canal (M3.5): lista à esquerda e conversa à direita. No celular são duas telas,
 * lista e conversa, e a conversa aberta fica na URL (`?conversa=<id>`), o que também serve de link da ficha do parceiro.
 */
export function Caixa({ canal }: { canal: Canal }) {
  const { t } = useTranslation('conversas');
  const [params, setParams] = useSearchParams();
  const id = params.get('conversa');
  const estado = useEstadoCanais();
  const s = useSessao();
  const [lista, setLista] = useState<EstadoLista>('carregando');
  // Depois de carregada, a lista só volta a "carregando" por troca de filtro: o painel não pisca.
  const aoEstadoLista = useCallback((e: EstadoLista) => setLista((anterior) => (e === 'carregando' && anterior === 'ok' ? 'ok' : e)), []);
  const intervalo = useIntervaloConversas(8000);
  const atual = useQuery({
    queryKey: ['conversas', 'conversa', id],
    queryFn: () => get<Conversa>(`/conversas/conversas/${id}`),
    enabled: !!id,
    refetchInterval: intervalo,
  });
  const conversa = id && atual.data?.canal === canal ? atual.data : null;

  // U11 (lote 1): abrir e voltar mexem só na conversa; o filtro da caixa (`?caixa=`) fica na URL.
  function abrir(c: Conversa) {
    setParams(
      (atual) => {
        const n = new URLSearchParams(atual);
        n.set('conversa', c.id);
        return n;
      },
      { replace: false },
    );
  }
  function voltar() {
    setParams(
      (atual) => {
        const n = new URLSearchParams(atual);
        n.delete('conversa');
        return n;
      },
      { replace: true },
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {estado.data && !estado.data[canal] && <AvisoConector texto={t(`aviso.${canal}`)} />}
      {canal === 'email' && estado.data?.email && <CaixasEmail />}
      {s.pode('conversas.enviar') && <PresencaAtendimento />}
      <div className="grid min-w-0 gap-3 md:h-168 md:grid-cols-5">
        <section className={`h-144 min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-borda bg-sup-1 md:col-span-2 md:flex md:h-full ${conversa ? 'hidden' : 'flex'}`}>
          <ListaConversas canal={canal} selecionada={id} aoEscolher={abrir} aoEstado={aoEstadoLista} />
        </section>
        <section className={`h-144 min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-borda bg-sup-1 md:col-span-3 md:flex md:h-full ${conversa ? 'flex' : 'hidden'}`}>
          {conversa ? (
            <PainelConversa key={conversa.id} conversa={conversa} estado={estado.data} aoVoltar={voltar} />
          ) : lista === 'ok' ? (
            <Vazio icone={<MessagesSquare className="size-5" aria-hidden />} titulo={t('painel.escolhaTitulo')} texto={t('painel.escolha')} />
          ) : lista === 'carregando' ? (
            <Carregando />
          ) : null}
        </section>
      </div>
    </div>
  );
}
